import { POIShape } from './POIShape';
import * as THREE from 'three';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';
import { ThreeGeomFactory } from './ThreeGeomFactory';
import { POI } from '../poi/POI';
import { MaterialSet } from '../materials/MaterialFactory';
import { Logger } from '../core/Logger';

export class TileShape extends POIShape {
    // Tracks aspect ratio of the last built geometry, so repeated change-events with the same
    // value skip the expensive recreateGeometry() pass.
    private _lastBuiltAspect: number | null = null;

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        //console.log(`🚨 [TileShape] CONSTRUCTOR called for POI: ${poi.name}`);
        super(poi, sceneSettings, appSettings);
        //console.log(`✅ [TileShape] CONSTRUCTOR completed for POI: ${poi.name}`);

        // When the material's aspect ratio changes, we need to rebuild the geometry
        // to maintain the correct proportions based on the texture.
        // Track last-built aspect to skip phantom recreate-with-same-value calls.
        this._lastBuiltAspect = null;
        this.primaryMaterial.aspectRatio.on((newVal) => {
            const tolerance = 0.001;
            if (this._lastBuiltAspect !== null && Math.abs((newVal ?? 1) - this._lastBuiltAspect) < tolerance) {
                // Aspect didn't actually change — skip the expensive recreate.
                return;
            }
            try { Logger.Instance.Info(`Tile ${this.poi.name}: aspectRatio changed -> ${newVal} (recreate)`, 'Tile'); } catch {}
            this.recreateGeometry();
        });
        // Also react to corner radius changes
        this.poi.poidata.cornerRadius?.on(() => this.recreateGeometry());
        // Also react to edge radius changes (for beveled edges)
        this.poi.poidata.edgeRadius?.on(() => this.recreateGeometry());
        // React to resolution changes
        this.sceneSettings?.geometryResolution.on(() => this.recreateGeometry());
        // Also react to depth changes (important for tile frames)
        this.poi.poidata.depth.on(() => this.recreateGeometry());
    }

    protected override createGeometry(): THREE.BufferGeometry {
        const cornerRadius = this.poi.poidata.cornerRadius?.value ?? 0.0;
        const edgeRadius = this.poi.poidata.edgeRadius?.value ?? 0.0;
        const resolution = this.sceneSettings?.geometryResolution.value ?? 1.0;
        
        // The geometry's aspect ratio is driven by the material's texture.
        const aspect = this.primaryMaterial.aspectRatio.value ?? 1.0;
        this._lastBuiltAspect = aspect;
        try { Logger.Instance.Info(`Tile ${this.poi.name}: createGeometry aspect=${aspect}`, 'Tile'); } catch {}

        const width = 1 * aspect;
        const height = 1;

        // Tile always has meaningful depth for the frame extrusion
        const depth = this.poi.poidata.depth.value ?? 0.1;

        // The radius must be relative to the new geometry's dimensions.
        const relativeCornerRadius = cornerRadius > 0 ? cornerRadius / Math.max(width, height) : 0;
        const relativeEdgeRadius = edgeRadius > 0 ? edgeRadius / Math.max(width, height, depth) : 0;

        // Get useBacksideSideMaterial setting for ThreeGeomFactory
        const useBacksideSideMaterial = this.poi.poidata.useBacksideSideMaterial?.value ?? false;

        const hasMultipleURIs = this.poi.poidata.uri.value && this.poi.poidata.uri.value.length > 1;
        
        // For tile: Use BoxGeometry when we need separate front/back face control
        if (hasMultipleURIs || useBacksideSideMaterial) {
            // Tile with multi-material: Use rounded BoxGeometry if corner radius is specified
            const uriCount = this.poi.poidata.uri.value?.length || 0;
            const reason = hasMultipleURIs ? `multi-URI (${uriCount} URIs)` : 'useBacksideSideMaterial=true';
            
            if (relativeCornerRadius > 0 || relativeEdgeRadius > 0) {
                //console.log(`🔧 [TileShape] Using RoundedBoxGeometry for tile: ${reason} (cornerRadius=${cornerRadius}, edgeRadius=${edgeRadius})`);
                return ThreeGeomFactory.createRoundedBoxWithBeveledEdges(
                    width, 
                    height, 
                    depth, 
                    relativeCornerRadius, 
                    relativeEdgeRadius, 
                    resolution,
                    aspect,
                    this.poi.poidata.textureBacksideFlipX?.value ?? true,
                    useBacksideSideMaterial
                );
            } else {
                //console.log(`🔧 [TileShape] Using BoxGeometry for tile: ${reason}`);
                return new THREE.BoxGeometry(width, height, depth);
            }
        }
        
        // Use beveled edges if edgeRadius is greater than 0 (single URI only)
        if (relativeEdgeRadius > 0) {
            return ThreeGeomFactory.createRoundedBoxWithBeveledEdges(
                width, 
                height, 
                depth, 
                relativeCornerRadius, 
                relativeEdgeRadius, 
                resolution,
                null, // textureAspectRatio (not used for boxes)
                this.poi.poidata.textureBacksideFlipX?.value ?? true,
                useBacksideSideMaterial
            );
        } else {
            return ThreeGeomFactory.createRoundedBox(
                width, 
                height, 
                depth, 
                relativeCornerRadius, 
                resolution,
                null, // textureAspectRatio (not used for boxes)
                this.poi.poidata.textureBacksideFlipX?.value ?? true,
                useBacksideSideMaterial
            );
        }
    }

    /**
     * 🎯 TILE-SPECIFIC: Override material group assignment
     */
    protected override assignMaterialGroupsToGeometry(materialSet: MaterialSet): void {
        if (!this._geometry || !this._geometry.groups || this._geometry.groups.length === 0) {
            //console.log(`❌ [TileShape] No geometry groups to assign materials to`);
            return;
        }

        const uris = this.poi.poidata.uri.value;
        const hasMultipleURIs = Array.isArray(uris) && uris.length > 1;
        const useBacksideSideMaterial = this.poi.poidata.useBacksideSideMaterial?.value ?? false;
        
        //console.log(`🚨🚨🚨 [TileShape] assignMaterialGroupsToGeometry() DEFINITELY CALLED! 🚨🚨🚨`);
        //console.log(`🔧 [TileShape] Assigning materials to ${this._geometry.groups.length}-group geometry`);
        //console.log(`  🎯 hasMultipleURIs: ${hasMultipleURIs}, useBacksideSideMaterial: ${useBacksideSideMaterial}`);
        //console.log(`  🔍 MaterialSet check: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);
        //console.log(`  🔍 URI debug: uris=${JSON.stringify(uris)}, length=${uris?.length}`);
        //console.log(`  🔍 Condition check: hasMultipleURIs=${hasMultipleURIs} && materialSet.back=${!!materialSet.back} = ${hasMultipleURIs && materialSet.back}`);
        
        // 🚨 BRUTAL TEST: FORCE Material[1] on ALL faces (should show pat2.png everywhere)
        //console.log(`🚨🚨🚨 BRUTAL FORCE: Setting ALL faces to Material[1] (pat2.png) 🚨🚨🚨`);
        for (let i = 0; i < this._geometry.groups.length; i++) {
            this._geometry.groups[i].materialIndex = 1; // Force Material[1] (pat2.png)
            //console.log(`  🔧 FORCED Group[${i}] = materialIndex 1`);
        }

        if (this._geometry.groups.length === 6) {
            // BoxGeometry case (used for multi-material scenarios)
            //console.log(`🎯 [TileShape] BoxGeometry mode (6 faces)`);
            //console.log(`  📦 BoxGeometry group mapping: [0]=Right, [1]=Left, [2]=Top, [3]=Bottom, [4]=Front, [5]=Back`);
            
            if (hasMultipleURIs && materialSet.back) {
                // 🚨 SIMPLE: Just make all faces use the FIRST material to test
                //console.log(`🚨 [TileShape] SUPER SIMPLE: ALL faces = Material[0] (first texture)`);
                for (let i = 0; i < 6; i++) {
                    this._geometry.groups[i].materialIndex = 0; // ALL = Material[0]
                }
                //console.log(`🎯 [TileShape] If this works, multi-material is the problem`)
            } else if (useBacksideSideMaterial && materialSet.sides) {
                // Single-URI + useBacksideSideMaterial: Front=texture, Back/Sides=solid
                //console.log(`🎯 [TileShape] Single-URI + useBacksideSideMaterial: Front=texture, Back/Sides=solid`);
                this._geometry.groups[0].materialIndex = 1; // Right = solid (sides)
                this._geometry.groups[1].materialIndex = 1; // Left = solid (sides)
                this._geometry.groups[2].materialIndex = 1; // Top = solid (sides)
                this._geometry.groups[3].materialIndex = 1; // Bottom = solid (sides)
                this._geometry.groups[4].materialIndex = 0; // Front = texture
                this._geometry.groups[5].materialIndex = 1; // Back = solid (sides)
            } else {
                // Default: All faces use same material
                //console.log(`❌ [TileShape] FELL BACK TO DEFAULT - All faces use front material!`);
                //console.log(`  🚨 This means multi-URI condition failed!`);
                //console.log(`  🔍 Debug: hasMultipleURIs=${hasMultipleURIs}, materialSet.back=${!!materialSet.back}, materialSet.sides=${!!materialSet.sides}`);
                //console.log(`  🎯 THIS IS WHY TEXTURE APPEARS ON RIGHT SIDE (Group[0])!`);
                for (let i = 0; i < 6; i++) {
                    this._geometry.groups[i].materialIndex = 0; // All faces get front texture
                    //console.log(`    Group[${i}] = materialIndex 0 (front texture)`);
                }
            }
        } else {
            // ExtrudeGeometry case (used for corner/edge radius)
            //console.log(`🎯 [TileShape] ExtrudeGeometry mode (${this._geometry.groups.length} groups)`);
            //console.log(`🔍 [TileShape] ExtrudeGeometry Debug: hasMultipleURIs=${hasMultipleURIs}, materials: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);
            
            // 🚨 FIX: Correct material assignment for ExtrudeGeometry
            // ExtrudeGeometry typically has: Group[0]=Front, Group[1]=Back+Sides combined, Group[2]=Bevels
            
            if (hasMultipleURIs && materialSet.back && materialSet.sides) {
                // Multi-URI case: front texture, back texture, sides solid
                //console.log(`🎯 [TileShape] ExtrudeGeometry Multi-URI case`);
                if (this._geometry.groups.length >= 3) {
                    this._geometry.groups[0].materialIndex = 0; // Front face = Material[0] (front texture)
                    this._geometry.groups[1].materialIndex = 1; // Back face = Material[1] (back texture) 
                    this._geometry.groups[2].materialIndex = 2; // Side faces = Material[2] (solid)
                    //console.log(`🎯 [TileShape] ExtrudeGeometry 3-group: Front=0(texture1), Back=1(texture2), Sides=2(solid)`);
                } else if (this._geometry.groups.length === 2) {
                    // Only 2 groups available: Front and Back+Sides combined
                    this._geometry.groups[0].materialIndex = 0; // Front = front texture
                    this._geometry.groups[1].materialIndex = 2; // Back+Sides = solid (since we can't separate them)
                    //console.log(`🎯 [TileShape] ExtrudeGeometry 2-group: Front=0(texture1), BackSides=2(solid)`);
                }
            } else if (useBacksideSideMaterial && materialSet.sides) {
                // Single-URI + useBacksideSideMaterial: front texture, back+sides solid
                //console.log(`🎯 [TileShape] ExtrudeGeometry useBacksideSideMaterial case`);
                this._geometry.groups[0].materialIndex = 0; // Front = texture
                if (this._geometry.groups.length > 1) {
                    this._geometry.groups[1].materialIndex = 1; // Back+Sides = solid
                }
                //console.log(`🎯 [TileShape] ExtrudeGeometry useBackside: Front=0(texture), BackSides=1(solid)`);
            } else {
                // Default: all faces use same material
                //console.log(`🎯 [TileShape] ExtrudeGeometry default case`);
                for (let i = 0; i < this._geometry.groups.length; i++) {
                    this._geometry.groups[i].materialIndex = 0;
                }
                //console.log(`🎯 [TileShape] ExtrudeGeometry single-mat: All=0`);
            }
        }
        
        // Debug final assignments
        const assignments = this._geometry.groups.map((group, i) => `group[${i}]=${group.materialIndex}`).join(', ');
        //console.log(`🎯 [TileShape] Final assignments: ${assignments}`);
    }
}